"use client";

import { AuthContext, useRuntimeConfig } from "@finbackoffice/site-core";
import { TranslationScopes } from "@finbackoffice/enums";
import { useTranslation } from "react-i18next";
import { ConfigContextV2, ModalsContextV2, UserAccountContextV2, WalletsContext } from "contexts";
import { useParams, useRouter } from "next/navigation";
import { useContext, useCallback, useMemo } from "react";
import { NotificationContext } from "contexts";
import { RouteNames } from "utils/constants";

export const useCasinoMethods = () => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { t } = useTranslation(TranslationScopes.Casino);
    const router = useRouter();
    const params = useParams();

    const { siteDefaultCurrency } = useContext(ConfigContextV2);
    const { userCurrency } = useContext(UserAccountContextV2);
    const { isUserLoggedIn } = useContext(AuthContext);
    const { notice } = useContext(NotificationContext);
    const { currentCurrency, isCurrentWalletIdValid } = useContext(WalletsContext);
    const { loginModalRef } = useContext(ModalsContextV2);

    const fiatEnabled = COMMON_SITE_CONFIGS.wallet.fiat;
    const defaultCurrency = useMemo(
        () => (!fiatEnabled ? COMMON_SITE_CONFIGS.wallet._crypto.default : siteDefaultCurrency),
        [fiatEnabled, COMMON_SITE_CONFIGS.wallet._crypto.default, siteDefaultCurrency],
    );

    const navigateToGame = useCallback(
        (gameId: string, isSlots: boolean, forFun: boolean | null = false) => {
            let subPath = "";
            const forFunQuery = `?forFun=${forFun}`;

            if (isSlots) {
                subPath = RouteNames.SLOTS;
            } else {
                subPath = RouteNames.LIVE_CASINO;
            }

            if (subPath) {
                router.push(
                    `/${params?.lang}/${RouteNames.CASINO}/${subPath}/${gameId}${forFun !== null ? forFunQuery : ""}`,
                );
            }
        },
        [params?.lang, router],
    );

    const showEmptyBalanceError = useCallback(() => {
        notice(
            {
                type: "warning",
                title: t("casino_no_balance_warning_title"),
                message: t("casino_no_balance_message", {
                    currentCurrency,
                    defaultCurrency,
                    userCurrency,
                }),
            },
            "modal",
        );
    }, [currentCurrency, defaultCurrency, userCurrency, notice, t]);

    const handlePlayNow = useCallback(
        (gameId: string, isSlots: boolean, onReady?: () => void) => {
            if (isUserLoggedIn) {
                if (isCurrentWalletIdValid) {
                    if (onReady && typeof onReady === "function") {
                        onReady();
                    } else {
                        navigateToGame(gameId, isSlots);
                    }
                } else {
                    showEmptyBalanceError();
                }
            } else {
                loginModalRef.current?.open();
            }
        },
        [
            isCurrentWalletIdValid,
            navigateToGame,
            showEmptyBalanceError,
            isUserLoggedIn,
            loginModalRef,
        ],
    );

    const handlePlayForFun = useCallback(
        (gameId: string, isSlots: boolean) => {
            navigateToGame(gameId, isSlots, true);
        },
        [navigateToGame],
    );

    return { handlePlayForFun, handlePlayNow, showEmptyBalanceError, navigateToGame };
};
